import { DirectiveOptions } from 'vue';

const bindEvents = (el: HTMLElement) => {
  el.addEventListener('mouseenter', onMouseEnter);
  el.addEventListener('mouseleave', onMouseLeave);
  el.addEventListener('click', onClick);
};

const unbindEvents = (el: HTMLElement) => {
  el.removeEventListener('mouseenter', onMouseEnter);
  el.removeEventListener('mouseleave', onMouseLeave);
  el.removeEventListener('click', onClick);
};

const onMouseEnter = (event: Event) => {
  show(event.currentTarget as HTMLElement);
};

const onMouseLeave = (event: Event) => {
  hide(event.currentTarget as HTMLElement);
};

const onClick = (event: Event) => {
  hide(event.currentTarget as HTMLElement);
};

const show = (eventTarget: HTMLElement) => {
  createTooltipElement(eventTarget);
  alignTooltip(eventTarget);
};

const hide = (eventTarget: HTMLElement) => {
  removeTooltipElement(eventTarget);
};

const createTooltipElement = (eventTarget: HTMLElement) => {
  const id = `tooltip_${Math.floor(Math.random() * 1000)}`;
  const dataset = eventTarget.dataset as TooltipDomDataSet;
  eventTarget.dataset.tooltipId = id;

  const tooltipContainer = document.createElement('div') as HTMLDivElement;
  tooltipContainer.id = id;
  tooltipContainer.classList.add('tooltip');
  tooltipContainer.classList.add(`tooltip--${dataset.tooltipStatus}`);
  tooltipContainer.classList.add(`tooltip--${dataset.tooltipPosition}`);

  const tooltipContent = document.createElement('div') as HTMLDivElement;
  tooltipContent.className = 'tooltip-content';
  tooltipContent.innerHTML = dataset.tooltipValue;
  tooltipContainer.appendChild(tooltipContent);

  const tooltipArrow = document.createElement('div') as HTMLDivElement;
  tooltipArrow.className = 'tooltip-arrow';
  tooltipContainer.appendChild(tooltipArrow);

  document.body.appendChild(tooltipContainer);
  tooltipContainer.style.display = 'inline-block';

  return tooltipContainer;
};

const removeTooltipElement = (eventTarget: HTMLElement) => {
  if (eventTarget) {
    const tooltipElement = document.getElementById(
      (eventTarget.dataset as TooltipDomDataSet).tooltipId,
    );
    if (tooltipElement != null && tooltipElement.parentElement != null) {
      document.body.removeChild(tooltipElement);
    }

    delete eventTarget.dataset.tooltipId;
  }
};

const alignTooltip = (eventTarget: HTMLElement) => {
  const dataset = eventTarget.dataset as TooltipDomDataSet;
  if (dataset.tooltipPosition = TooltipPosition.TOP) {
    alignTop(eventTarget);
  }
};

const alignTop = (eventTarget: HTMLElement, offset = 8) => {
  const tooltipElement = document.getElementById(
    (eventTarget.dataset as TooltipDomDataSet).tooltipId,
  );
  const targetTop = eventTarget.getBoundingClientRect().top;
  const tooltipHeight = tooltipElement.getBoundingClientRect().height;

  const targetWidth = eventTarget.getBoundingClientRect().width;
  const targetLeft = eventTarget.getBoundingClientRect().left;
  const tooltipWidth = tooltipElement.getBoundingClientRect().width;

  tooltipElement.style.top = `${(targetTop - tooltipHeight) - offset}px`;
  tooltipElement.style.left = `${targetLeft + (targetWidth - tooltipWidth) / 2}px`;
};

const getPosition = (modifiers: Record<string, boolean>) => {
  const position = Object.keys(TooltipPosition).find(
    (possiblePositions) => modifiers[possiblePositions] != null
      && modifiers[possiblePositions],
  );
  return position != null ? position : 'top';
};

const getStatus = (modifiers: Record<string, boolean>) => {
  const status = Object.keys(TooltipStatus).find(
    (possibleStatus) => modifiers[possibleStatus] != null && modifiers[possibleStatus],
  );
  return status != null ? status : 'default';
};

export const Tooltip: DirectiveOptions = {
  bind(el, options) {
    let tooltipValue;

    if (typeof options.value === 'string') {
      tooltipValue = options.value;
    } else {
      tooltipValue = options.value.value;
    }

    const dataset = el.dataset as TooltipDomDataSet;

    dataset.tooltipValue = tooltipValue;
    dataset.tooltipPosition = getPosition(options.modifiers);
    dataset.tooltipStatus = getStatus(options.modifiers);

    bindEvents(el);
  },
  unbind(el) {
    unbindEvents(el);
    delete el.dataset.tooltipValue;
    delete el.dataset.tooltipPosition;
    delete el.dataset.tooltipStatus;
  },
  update(el, options) {
    let tooltipValue;

    if (typeof options.value === 'string') {
      tooltipValue = options.value;
    } else {
      tooltipValue = options.value.value;
    }

    const dataset = el.dataset as TooltipDomDataSet;
    dataset.tooltipValue = tooltipValue;
    dataset.tooltipPosition = getPosition(options.modifiers);
    dataset.tooltipStatus = getStatus(options.modifiers);
  },
};

export default Tooltip;

interface TooltipDomDataSet extends DOMStringMap {
  tooltipValue: string;
  tooltipId: string;
  tooltipPosition: string;
  tooltipStatus: string;
}

export enum TooltipPosition{
  TOP = 'top',
  // TODO: extend other positions
}

export enum TooltipStatus {
  DEFAULT = 'default',
  // TODO: extend other status
}
